/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

const CheckoutContext = React.createContext()

export const CheckoutProvider = ({children}) => {
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const einstein = useEinstein()
    const [step, setStep] = useState()
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState()
    const [checkoutError, setCheckoutError] = useState()

    const CHECKOUT_STEPS_LIST = [
        'CONTACT_INFO',
        'SHIPPING_ADDRESS',
        'SHIPPING_OPTIONS',
        'PAYMENT',
        'REVIEW_ORDER'
    ]
    const STEPS = CHECKOUT_STEPS_LIST.reduce((acc, step, idx) => ({...acc, [step]: idx}), {})

    const getCheckoutStepName = (step) => CHECKOUT_STEPS_LIST[step]

    const hasOnlyEgiftCards = useMemo(() => {
        return !basket?.productItems && basket?.giftCertificateItems?.length > 0
    }, [basket?.productItems, basket?.giftCertificateItems])

    const nonStorePickShipments = useMemo(() => {
        const shipments = basket?.shipments
        return shipments?.length > 0
            ? shipments.filter((shipment) => !('c_shipmentType' in shipment))
            : []
    }, [basket?.productItems, basket?.shipments])

    const hasPaypalPaymentInstuments = useMemo(() => {
        if (!basket?.paymentInstruments) return false
        const ppPaymentInstrument = basket.paymentInstruments.filter(
            (pi) => pi.paymentMethodId === 'PayPal'
        )
        return !!ppPaymentInstrument?.length
    }, [basket.paymentInstruments])

    useEffect(() => {
        if (!customer || !basket) {
            return
        }
        const isOrderTotalZero = basket.orderTotal === 0
        let keepInPaymentsection = !basket.paymentInstruments || !basket.billingAddress
        if (isOrderTotalZero && basket.billingAddress && basket.paymentInstruments) {
            keepInPaymentsection = false
        } else if (!keepInPaymentsection) {
            const paymentInstruments = basket.paymentInstruments
            const ccPaymentInstrument = paymentInstruments.filter(
                (pi) => pi.paymentMethodId !== 'PayPal' && pi.paymentMethodId !== 'GIFT_CERTIFICATE'
            )

            if (
                ccPaymentInstrument?.amount > 0 ||
                paymentInstruments?.length === ccPaymentInstrument?.length
            ) {
                keepInPaymentsection = true
            } else {
                const ppPaymentInstrument = paymentInstruments.filter(
                    (pi) => pi.paymentMethodId === 'PayPal'
                )

                if (!ppPaymentInstrument.length) {
                    const giftCardTotal = basket.paymentInstruments.reduce((total, instrument) => {
                        if (instrument?.paymentMethodId === 'GIFT_CERTIFICATE') {
                            const amount = parseFloat(instrument.amount) || 0 // adjust if `amount` is stored differently
                            return total + amount
                        }
                        return total
                    }, 0)

                    keepInPaymentsection = basket.orderTotal - giftCardTotal > 0
                }
            }
        }

        let step = STEPS.REVIEW_ORDER

        if (customer.isGuest && !basket.customerInfo?.email) {
            step = STEPS.CONTACT_INFO
        } else if (
            !hasOnlyEgiftCards &&
            nonStorePickShipments.length &&
            !nonStorePickShipments[0]?.shippingAddress
        ) {
            step = STEPS.SHIPPING_ADDRESS
        } else if (
            !hasOnlyEgiftCards &&
            nonStorePickShipments.length &&
            !nonStorePickShipments[0]?.shippingMethod
        ) {
            step = STEPS.SHIPPING_OPTIONS
        } else if (keepInPaymentsection) {
            step = STEPS.PAYMENT
        }

        setStep(step)
    }, [
        customer?.isGuest,
        basket?.customerInfo?.email,
        basket?.shipments[0]?.shippingAddress,
        basket?.shipments[0]?.shippingMethod,
        basket?.paymentInstruments,
        basket?.billingAddress
    ])

    /**************** Einstein ****************/
    // Run this once when checkout begins
    useEffect(() => {
        if (basket?.productItems) {
            einstein.sendBeginCheckout(basket)
        }
    }, [])

    // Run this every time checkout steps change
    useEffect(() => {
        if (step != undefined) {
            einstein.sendCheckoutStep(getCheckoutStepName(step), step, basket)
        }
    }, [step])

    const value = {
        step,
        STEPS,
        hasOnlyEgiftCards,
        nonStorePickShipments,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        hasPaypalPaymentInstuments,
        checkoutError,
        setCheckoutError,
        goToNextStep: () => setStep(step + 1),
        goToStep: (step) => setStep(step)
    }

    return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>
}

CheckoutProvider.propTypes = {
    children: PropTypes.any
}

/**
 * A hook for managing checkout state and actions
 * @returns {Object} Checkout data and actions
 */
export const useCheckout = () => {
    return React.useContext(CheckoutContext)
}
